/**
 * Labs: AI Preview. Pick any customer to see what the AI pilot would read from
 * their website and landing pages, push to their marketing GHL contact and
 * write to them, and change the email prompts. It reads only: scans run here
 * aren't stored, nothing is written to GHL, preview emails aren't saved or
 * sent. The one write is "Save for the email job" on the prompts tab.
 *
 * To remove the page: see "Removing the AI Preview page" in docs/runbooks/ai-pilot.md.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';

import { AgencyCombobox } from '@/components/AgencyCombobox';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useIsViewer } from '@/hooks/useIsViewer';
import { cn } from '@/lib/utils';
import toastService from '@/services/toast/service';
import {
  aiPreviewService,
  type EmailPreview,
  type PreviewAccount,
  type PreviewScans,
  type PromptsOverview,
} from '@/services/aiPreview';
import { EmailTab } from './EmailTab';
import { PromptsTab } from './PromptsTab';
import { PushTab } from './PushTab';
import { ScanTab } from './ScanTab';
import {
  COMPANY_KEY,
  STAGE_LABELS,
  TRIAL_RISK_LABELS,
  capitalize,
  entryFromResult,
  entryFromSaved,
  failedWithoutMessage,
  type ScanEntry,
} from './shared';

const DAY_MS = 86_400_000;
/** "Scan checked" scans this many pages at once. */
const SCAN_CONCURRENCY = 3;

function useFormatDate() {
  const { user } = useAuth();
  const timeZone = user?.timezone || undefined;
  return (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleString('en-US', { timeZone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
      : null;
}

export default function AiPreviewPage() {
  const [params, setParams] = useSearchParams();
  const publicId = params.get('agency') ?? '';
  const { user } = useAuth();
  // Previews change nothing, so viewers run them too; internal viewers stay read-only, as everywhere.
  const canRun = user?.role !== 'internal_viewer';
  const canSave = !useIsViewer();
  const formatDate = useFormatDate();

  const [tab, setTab] = useState('scan');
  const [account, setAccount] = useState<PreviewAccount | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entries, setEntries] = useState<Record<string, ScanEntry>>({});
  const [scanning, setScanning] = useState<Set<string>>(new Set());
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const [email, setEmail] = useState<EmailPreview | null>(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailChoice, setEmailChoice] = useState<string | null>(null);

  const [prompts, setPrompts] = useState<PromptsOverview | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [promptSection, setPromptSection] = useState('style');
  // A scan or email that finishes after another customer was picked belongs to the old one: drop it.
  const currentId = useRef(publicId);
  currentId.current = publicId;

  useEffect(() => {
    aiPreviewService.getPrompts().then(setPrompts).catch(() => setPrompts(null));
  }, []);

  useEffect(() => {
    setAccount(null);
    setEntries({});
    setScanning(new Set());
    setChecked(new Set());
    setEmail(null);
    setEmailLoading(false);
    setEmailError(null);
    setEmailChoice(null);
    if (!publicId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    aiPreviewService
      .getAccount(publicId)
      .then((a) => {
        if (cancelled) return;
        setAccount(a);
        // Start from what the AI pilot already saved; scans run here replace them on this page only.
        const saved: Record<string, ScanEntry> = {};
        if (a.company.saved) saved[COMPANY_KEY] = entryFromSaved(a.company.saved);
        for (const p of a.pages) if (p.saved) saved[p.url] = entryFromSaved(p.saved);
        setEntries(saved);
        setChecked(new Set(a.pages.filter((p) => p.saved?.status === 'done').map((p) => p.url)));
      })
      .catch((err) => !cancelled && setError(err.response?.data?.message || "Couldn't load this customer"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [publicId]);

  const scans: PreviewScans = useMemo(() => {
    const company = entries[COMPANY_KEY];
    const done = (e: ScanEntry | undefined) =>
      e?.status === 'done' && e.summary ? { url: e.url, scannedAt: e.scannedAt ?? new Date().toISOString(), summary: e.summary } : null;
    return {
      company: done(company),
      funnels: [...checked].map((url) => done(entries[url])).filter((s) => s !== null),
    };
  }, [entries, checked]);
  const scansKey = JSON.stringify(scans);

  const scan = async (key: string) => {
    if (!account) return;
    const id = account.agency.publicId;
    setScanning((s) => new Set(s).add(key));
    try {
      const result = await aiPreviewService.scan(id, key === COMPANY_KEY ? { kind: 'company' } : { kind: 'funnel', url: key });
      if (currentId.current !== id) return;
      setEntries((e) => ({ ...e, [key]: entryFromResult(result) }));
      if (key !== COMPANY_KEY && result.status === 'done') setChecked((c) => new Set(c).add(key));
    } catch (err) {
      // The API client shows errors that carry a message.
      if (currentId.current === id && failedWithoutMessage(err)) toastService.error("The scan didn't finish. Try again.");
    } finally {
      if (currentId.current === id) {
        setScanning((s) => {
          const next = new Set(s);
          next.delete(key);
          return next;
        });
      }
    }
  };

  const scanChecked = async () => {
    const queue = [...checked];
    const worker = async () => {
      for (let url = queue.shift(); url; url = queue.shift()) await scan(url);
    };
    await Promise.all(Array.from({ length: SCAN_CONCURRENCY }, worker));
  };

  const writeEmail = async () => {
    if (!account) return;
    const id = account.agency.publicId;
    setEmailLoading(true);
    setEmailError(null);
    try {
      const preview = await aiPreviewService.email(id, { scans, emailKey: emailChoice ?? undefined });
      if (currentId.current === id) setEmail(preview);
    } catch (err) {
      if (currentId.current === id) {
        setEmailError((err as { response?: { data?: { message?: string } } }).response?.data?.message || "Couldn't write the email");
      }
    } finally {
      if (currentId.current === id) setEmailLoading(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 w-full max-w-[1310px] mx-auto px-4 sm:px-0">
      <div className="flex flex-col gap-2">
        <span className="text-[13px] text-muted-foreground">Labs / AI Preview</span>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">AI Preview</h1>
          <Badge tone="info">Preview only · nothing is saved to GHL or sent</Badge>
        </div>
        <p className="text-sm text-muted-foreground max-w-3xl">
          Pick any customer to see what the AI pilot would read from their website, push to their marketing GHL contact, and write to them. It reads real
          data and changes nothing.
        </p>
      </div>

      <Card className="gap-1.5 px-4 py-4">
        <label htmlFor="ai-preview-customer" className="text-[13px] font-medium">
          Customer
        </label>
        <div className="max-w-xl">
          <AgencyCombobox
            id="ai-preview-customer"
            value={publicId}
            onChange={(id) => setParams(id ? { agency: id } : {})}
            placeholder="Search agencies"
          />
        </div>
        {account && (
          <span className="text-xs text-muted-foreground">
            {[account.agency.name, account.owner?.email].filter(Boolean).join(' · ')}
          </span>
        )}
      </Card>

      {loading && (
        <div role="status" className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
          <span className="sr-only">Loading</span>
        </div>
      )}
      {error && (
        <Card className="py-4 px-5 flex-row items-center gap-3 text-sm">
          <AlertCircle className="size-4 text-destructive" />
          {error}
        </Card>
      )}

      {account && <Facts account={account} />}

      {(account || (!publicId && prompts)) && (
        <Tabs value={account ? tab : 'prompts'} onValueChange={setTab} className="gap-5">
          <TabsList variant="line" className="w-full justify-start border-b rounded-none overflow-x-auto">
            {account && <TabsTrigger value="scan">Website scan</TabsTrigger>}
            {account && <TabsTrigger value="push">What would be pushed</TabsTrigger>}
            {account && <TabsTrigger value="email">The email</TabsTrigger>}
            <TabsTrigger value="prompts">Edit prompts</TabsTrigger>
          </TabsList>

          {account && (
            <TabsContent value="scan">
              <ScanTab
                account={account}
                entries={entries}
                scanning={scanning}
                checked={checked}
                canRun={canRun}
                formatDate={formatDate}
                onToggle={(url) =>
                  setChecked((c) => {
                    const next = new Set(c);
                    if (next.has(url)) next.delete(url);
                    else next.add(url);
                    return next;
                  })
                }
                onScan={scan}
                onScanChecked={scanChecked}
              />
            </TabsContent>
          )}
          {account && (
            <TabsContent value="push">
              <PushTab publicId={account.agency.publicId} scans={scans} scansKey={scansKey} canRun={canRun} />
            </TabsContent>
          )}
          {account && (
            <TabsContent value="email">
              <EmailTab
                account={account}
                emails={prompts?.emails ?? []}
                preview={email}
                loading={emailLoading}
                error={emailError}
                choice={emailChoice}
                canRun={canRun}
                onChoose={setEmailChoice}
                onWrite={writeEmail}
              />
            </TabsContent>
          )}
          <TabsContent value="prompts">
            {prompts ? (
              <PromptsTab
                overview={prompts}
                drafts={drafts}
                onDrafts={setDrafts}
                onOverview={setPrompts}
                selected={promptSection}
                onSelect={setPromptSection}
                account={account ? { publicId: account.agency.publicId, name: account.agency.name } : null}
                scans={scans}
                canRun={canRun}
                canSave={canSave}
                formatDate={formatDate}
              />
            ) : (
              <div role="status" className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function Facts({ account }: { account: PreviewAccount }) {
  const { trial } = account;
  let trialDay: string | null = null;
  if (account.billing === 'trialing' && trial.startsAt && trial.endsAt) {
    const length = Math.max(1, Math.round((Date.parse(trial.endsAt) - Date.parse(trial.startsAt)) / DAY_MS));
    const day = Math.min(length, Math.max(1, Math.floor((Date.now() - Date.parse(trial.startsAt)) / DAY_MS) + 1));
    trialDay = `Day ${day} of ${length}`;
  }
  const risk = TRIAL_RISK_LABELS[account.trialRisk];

  const facts: Array<{ label: string; value: string; detail: string; warn?: boolean }> = [
    { label: 'Plan', value: `${capitalize(account.plan)} · ${account.billing}`, detail: trialDay ?? (account.owner?.email || 'No owner') },
    { label: 'Lifecycle stage', value: STAGE_LABELS[account.stage] ?? account.stage, detail: account.stage },
    {
      label: 'Trial risk',
      value: risk?.label ?? 'Not scored',
      detail: risk?.detail ?? 'Only trials are scored',
      warn: risk?.warn,
    },
    {
      label: 'Forms',
      value: `${account.forms.total} ${account.forms.total === 1 ? 'form' : 'forms'}`,
      detail: `${account.forms.embedded} embedded on a page`,
    },
    {
      label: 'Main account',
      value: account.main?.name ?? 'No subaccount',
      detail: account.main ? `Oldest of ${account.main.subaccounts} ${account.main.subaccounts === 1 ? 'subaccount' : 'subaccounts'}` : '',
    },
    {
      label: 'In the AI pilot',
      value: account.inPilot ? 'Yes' : 'No',
      detail: account.inGhl ? 'Preview works for anyone' : 'Not in the marketing GHL yet',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
      {facts.map((f) => (
        <Card key={f.label} className="gap-1 px-3.5 py-3">
          <span className="text-xs text-muted-foreground">{f.label}</span>
          <span className={cn('font-semibold text-sm', f.warn && 'text-amber-700 dark:text-amber-400')}>{f.value}</span>
          <span className="text-xs text-muted-foreground truncate">{f.detail}</span>
        </Card>
      ))}
    </div>
  );
}
